import { AttFileControllerService } from '@/__generated__/CommonApi';
import { PartyForUnicefControllerService } from '@/__generated__/FrontApi';
import AttachmentPreview from '@/components/AttachmentImage';
import Button from '@/components/Button';
import ErrorMessage from '@/components/Form/ErrorMessage';
import FormGroup from '@/components/Form/FormGroup';
import LabelText from '@/components/Form/LabelText';
import Row from '@/components/Grid/Row';
import InputCheckbox from '@/components/Input/InputCheckbox';
import InputText from '@/components/Input/InputText';
import InputTextarea from '@/components/Input/InputTextarea';
import { Tit } from '@/components/Titles';
import { breakpoint } from '@/helpers/BreakpointHelper';
import { emailRegex, juminRegex } from '@/helpers/ValidationHelper';
import { usePopupStore } from '@/stores/PopupStore';
import React, { FC, useCallback, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';

const StepContainer = styled.div`
  header {
    line-height: 1.5;
    border-bottom: 1px solid #1cabe2;
    padding-bottom: 16px;
    margin-bottom: 24px;
  }
  ${FormGroup} {
    position: relative;
    z-index: 1;

    &.flex {
      .col {
        width: 50%;
      }
    }
  }
  article {
    &.a2 {
      position: relative;
      padding-top: 32px;
      padding-bottom: 24px;
      & + .a2 {
        border-top: 1px solid #d9d9d9;
      }

      &::before {
        content: '';
        display: block;
        width: 880px;
        height: 100%;
        background: #f8f9fa;
        position: absolute;
        left: 50%;
        top: 0;
        transform: translate(-50%, 0);
      }
    }
    &.a3 {
      padding-top: 24px;
    }
    &:last-child {
      border-bottom: 0;
      margin-bottom: 0;
    }
  }
  ${FormGroup} {
    .tip {
      font-size: 14px;
      margin-top: 8px;
      font-weight: 100;
      ${breakpoint(640)} {
        font-size: 12px;
      }
    }
    .billRequire {
      display: block;
      label {
        margin-right: 32px;
      }
    }

    &.flex {
      margin-bottom: 24px;
      .col1 {
        width: 192px;
      }
      .col2 {
        width: calc(100% - 192px);
      }
    }
  }
  .btn-wrap {
    margin-top: 80px;
  }
  ${breakpoint(`mobile`)} {
    ${FormGroup} {
      &.flex {
        margin-bottom: 24px;
        .col1,
        .col2 {
          width: 100%;
        }
        .col1 {
          margin-bottom: 24px;
        }
      }
      &.m-flex-disabled {
        display: block;
        margin: 0;
        .col {
          width: 100%;
          padding: 0;
          margin-bottom: 24px;
        }
      }
    }
  }
`;

export interface PartyReviewStep2Data {
  fundRaised: string;
  fundParty: string;
  receiptParticipant?: string;
  receiptName?: Array<string>;
  juminNumber?: Array<string>;
  amount?: Array<string>;
  receiptEmail?: Array<string>;
  answerOrder?: string;
  answerFavorite?: string;
  answerDifficult?: string;
  answerSecret?: string;
  answerSuggestion?: string;
  answerEtc?: string;
  isAgreeYn1: string | boolean;
  isAgreeYn2: string | boolean;
  photo1?: string;
  photo2?: string;
  photo3?: string;
  photo4?: string;
  photo5?: string;
}

interface PartyReviewCompleteProps {
  onSubmit: (data: PartyReviewStep2Data) => void;
  data?: any;
}

const ParrtAppyStep2: FC<PartyReviewCompleteProps> = ({
  onSubmit,
  ...props
}) => {
  const [receiptApply, setReceiptApply] = useState(false);
  const [receiptAll, setReceiptAll] = useState(false);
  const [receiptNum, setReceiptNum] = useState<number>(0);
  const [receiptAllNum, setReceiptAllNum] = useState<number>(0);
  const [receiptCustom, setReceiptCustom] = useState<number>(0);
  const [customApply, setCustomApply] = useState(false);
  // 첨부파일
  const [files, setFiles] = useState<File[]>([]);

  const {
    handleSubmit,
    register,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<PartyReviewStep2Data>({
    defaultValues: {
      fundRaised: ``,
      fundParty: ``,
      receiptParticipant: ``,
      receiptName: [],
      juminNumber: [],
      amount: [],
      receiptEmail: [],
      answerOrder: ``,
      answerFavorite: ``,
      answerDifficult: ``,
      answerSecret: ``,
      answerSuggestion: ``,
      answerEtc: ``,
      isAgreeYn1: false,
      isAgreeYn2: false,
      photo1: ``,
    },
  });
  const currentChild = (data: number) => {
    props.currentReviewParent(data);
  };

  const popupStore = usePopupStore();

  const onSubmitForm: SubmitHandler<PartyReviewStep2Data> = useCallback(
    async ({ isAgreeYn1, isAgreeYn2, ...formData }) => {
      const totalSubmit = {
        isAgreeYn1: isAgreeYn1 ? `Y` : `N`,
        isAgreeYn2: isAgreeYn2 ? `Y` : `N`,
        ...formData,
        ...props.totalReviewData,
      };

      try {
        let photo1: any;
        // 파일이 있다면
        if (files.length > 0) {
          const result = await AttFileControllerService.uploadUsingPost({
            file: files,
          });
          photo1 = result.data.attGrpNo;
        }
        const {
          resultCode,
          resultMessage,
        } = await PartyForUnicefControllerService.partyEndWriteUsingPost({
          ...totalSubmit,
          photo1,
        });
        reset();
        onSubmit(photo1);
        currentChild(3);
        // popupStore.show(resultMessage);
      } catch {
        console.log(totalSubmit);
        popupStore.show(
          `Party for UNICEF 후기 작성에 실패했습니다. 다시 시도해주세요.`,
        );
      }
    },
    [currentChild, files, popupStore, reset],
  );

  return (
    <StepContainer>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <div className="form-container">
          <header>
            <h3>
              <Tit size="s2">2. 파티 종료 후기</Tit>
            </h3>
          </header>
          <article>
            <FormGroup className="FormGroup flex">
              <div className="col">
                <LabelText require>총 모금액(원)</LabelText>
                <InputText
                  placeholder="총 모금액 입력"
                  ref={register({
                    required: {
                      value: true,
                      message: `총 모금액을 입력해주세요.`,
                    },
                    pattern: {
                      value: /^[0-9]/g,
                      message: `숫자만 입력해주세요.`,
                    },
                    maxLength: {
                      value: 12,
                      message: `12자 이내로 입력해주세요.`,
                    },
                  })}
                  name="fundRaised"
                  type="number"
                />
                {errors.fundRaised && (
                  <ErrorMessage>{errors.fundRaised.message}</ErrorMessage>
                )}
              </div>
              <div className="col">
                <LabelText require>참여 인원(명)</LabelText>
                <InputText
                  type="number"
                  placeholder="참여 인원수 입력"
                  ref={register({
                    required: {
                      value: true,
                      message: `참여 인원수를 입력해주세요.`,
                    },
                    pattern: {
                      value: /^[0-9]/g,
                      message: `숫자만 입력해주세요.`,
                    },
                    maxLength: {
                      value: 20,
                      message: `20자 이내로 입력해주세요.`,
                    },
                  })}
                  name="fundParty"
                  onKeyUp={(e) => {
                    setReceiptNum(Number(e.target.value));
                    setReceiptAllNum(Number(e.target.value));
                  }}
                />
                {errors.fundParty && (
                  <ErrorMessage>{errors.fundParty.message}</ErrorMessage>
                )}
              </div>
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText>기부금 영수증</LabelText>
              <Row className="billRequire">
                <InputCheckbox
                  label="신청"
                  onChange={() =>
                    receiptApply === false
                      ? setReceiptApply(true)
                      : setReceiptApply(false)
                  }
                />
                {receiptApply === true ? (
                  <InputCheckbox
                    label="참여자 전체"
                    onChange={() =>
                      receiptAll === false
                        ? setReceiptAll(true)
                        : setReceiptAll(false)
                    }
                  />
                ) : null}
              </Row>
            </FormGroup>
            {receiptApply === true ? (
              <FormGroup className="FormGroup">
                <LabelText require>신청 인원(명)</LabelText>
                <Row className="certify">
                  <div className="col-right">
                    {receiptAll === true ? (
                      <InputText
                        placeholder="신청 인원수 입력"
                        value={receiptNum}
                        ref={register({
                          required: {
                            value: true,
                            message: `신청 인원수를 입력해주세요.`,
                          },
                          pattern: {
                            value: /^[0-9]/g,
                            message: `숫자만 입력해주세요.`,
                          },
                        })}
                        name="receiptParticipant"
                        readOnly
                      />
                    ) : (
                      <InputText
                        placeholder="신청 인원수 입력"
                        ref={register({
                          required: {
                            value: true,
                            message: `신청 인원수를 입력해주세요.`,
                          },
                          pattern: {
                            value: /^[0-9]/g,
                            message: `숫자만 입력해주세요.`,
                          },
                        })}
                        name="receiptParticipant"
                        onKeyUp={(e) => {
                          setReceiptCustom(Number(e.target.value));
                        }}
                      />
                    )}
                  </div>
                  <div className="col-left">
                    <Button
                      type="button"
                      outline
                      disabled={receiptAll}
                      onClick={() => {
                        setReceiptApply(true);
                        setCustomApply(true);
                      }}
                    >
                      입력
                    </Button>
                  </div>
                </Row>
                {errors.receiptParticipant && (
                  <ErrorMessage>
                    {errors.receiptParticipant.message}
                  </ErrorMessage>
                )}
              </FormGroup>
            ) : null}
          </article>
          {receiptApply === true &&
          receiptAll === false &&
          customApply === false ? ( // 신청만 체크 시
            <article className="a2">
              <FormGroup className="FormGroup flex m-flex-disabled">
                <div className="col">
                  <LabelText require>성명</LabelText>
                  <InputText
                    placeholder="성명 입력"
                    ref={register({
                      maxLength: {
                        value: 8,
                        message: `8자 이내로 입력해주세요.`,
                      },
                    })}
                    name="receiptName[0]"
                  />
                  {errors.receiptName && (
                    <ErrorMessage>{errors.receiptName[0].message}</ErrorMessage>
                  )}
                </div>
                <div className="col">
                  <LabelText require>주민등록번호</LabelText>
                  <InputText
                    type="number"
                    placeholder="주민등록번호 입력 ( &lsquo;-&rsquo; 제외 )"
                    ref={register({
                      pattern: {
                        value: juminRegex,
                        message: `13자리 숫자로 입력해주세요.`,
                      },
                    })}
                    name="juminNumber[0]"
                  />
                  {errors.juminNumber && (
                    <ErrorMessage>{errors.juminNumber[0].message}</ErrorMessage>
                  )}
                </div>
              </FormGroup>
              <FormGroup className="FormGroup flex m-flex-disabled">
                <div className="col">
                  <LabelText require>후원금액(원)</LabelText>
                  <InputText
                    type="number"
                    placeholder="후원금액 입력"
                    ref={register({
                      pattern: {
                        value: /^[0-9]/g,
                        message: `숫자로만 입력해주세요.`,
                      },
                      maxLength: {
                        value: 10,
                        message: `10자 이내로 입력해주세요.`,
                      },
                    })}
                    name="amount[0]"
                  />
                  {errors.amount && (
                    <ErrorMessage>{errors.amount[0].message}</ErrorMessage>
                  )}
                </div>
                <div className="col">
                  <LabelText require>이메일</LabelText>
                  <InputText
                    placeholder="이메일주소 입력"
                    ref={register({
                      pattern: {
                        value: emailRegex,
                        message: `올바르지 않은 이메일 형식입니다.`,
                      },
                    })}
                    name="receiptEmail[0]"
                  />
                  {errors.receiptEmail && (
                    <ErrorMessage>
                      {errors.receiptEmail[0].message}
                    </ErrorMessage>
                  )}
                </div>
              </FormGroup>
            </article>
          ) : null}
          {receiptApply === true && receiptAll === true && customApply === false // 참여자 전체 신청 체크 시
            ? [...Array(receiptNum)].map((n, index) => (
                <article className="a2">
                  <FormGroup className="FormGroup flex m-flex-disabled">
                    <div className="col">
                      <LabelText require>성명</LabelText>
                      <InputText
                        placeholder="성명 입력"
                        ref={register({
                          maxLength: 8,
                        })}
                        name={`receiptName[${index}]`}
                        defaultValue={n}
                      />
                    </div>
                    <div className="col">
                      <LabelText require>주민등록번호</LabelText>
                      <InputText
                        placeholder="주민등록번호 입력 ( &lsquo;-&rsquo; 제외 )"
                        ref={register({
                          pattern: juminRegex,
                        })}
                        name={`juminNumber[${index}]`}
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="FormGroup flex m-flex-disabled">
                    <div className="col">
                      <LabelText require>후원금액(원)</LabelText>
                      <InputText
                        placeholder="후원금액 입력"
                        ref={register({
                          pattern: {
                            value: /^[0-9]/g,
                            message: `숫자로만 입력해주세요.`,
                          },
                          maxLength: {
                            value: 10,
                            message: `10자 이내로 입력해주세요.`,
                          },
                        })}
                        name={`amount[${index}]`}
                      />
                    </div>
                    <div className="col">
                      <LabelText require>이메일</LabelText>
                      <InputText
                        placeholder="이메일주소 입력"
                        ref={register({
                          pattern: {
                            value: emailRegex,
                            message: `올바르지 않은 이메일 형식입니다.`,
                          },
                        })}
                        name={`receiptEmail[${index}]`}
                      />
                    </div>
                  </FormGroup>
                </article>
              ))
            : null}
          {receiptApply === true && receiptAll === false && customApply === true // 참여자 직접입력 신청 체크 시
            ? [...Array(receiptCustom)].map((n, index) => (
                <article className="a2">
                  <FormGroup className="FormGroup flex m-flex-disabled">
                    <div className="col">
                      <LabelText require>성명</LabelText>
                      <InputText
                        placeholder="성명 입력"
                        ref={register}
                        name={`receiptName[${index}]`}
                      />
                    </div>
                    <div className="col">
                      <LabelText require>주민등록번호</LabelText>
                      <InputText
                        placeholder="주민등록번호 입력 ( &lsquo;-&rsquo; 제외 )"
                        ref={register}
                        name={`juminNumber[${index}]`}
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="FormGroup flex m-flex-disabled">
                    <div className="col">
                      <LabelText require>후원금액(원)</LabelText>
                      <InputText
                        placeholder="후원금액 입력"
                        ref={register}
                        name={`amount[${index}]`}
                      />
                    </div>
                    <div className="col">
                      <LabelText require>이메일</LabelText>
                      <InputText
                        placeholder="이메일주소 입력"
                        ref={register}
                        name={`receiptEmail[${index}]`}
                      />
                    </div>
                  </FormGroup>
                </article>
              ))
            : null}
          <article className="a3">
            <FormGroup className="FormGroup">
              <LabelText require>
                1 . 모금 파티는 어떻게 진행되었나요? (시간 순으로 자세히
                알려주세요.)
              </LabelText>
              <InputTextarea
                placeholder="파티가 어떤 순서로 진행되었는지 입력해 주세요."
                ref={register({
                  required: {
                    value: true,
                    message: `모금 파티의 진행 순서를 입력해주세요.`,
                  },
                })}
                name="answerOrder"
              />
              {errors.answerOrder && (
                <ErrorMessage>{errors.answerOrder.message}</ErrorMessage>
              )}
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>
                2 . 활동중 가장 즐거웠던 점은 무엇인가요?
              </LabelText>
              <InputTextarea
                placeholder="파티를 진행하며 가장 즐거웠던 점을 알려주세요."
                ref={register({
                  required: {
                    value: true,
                    message: `활동 중 즐거웠던 점을 입력해주세요.`,
                  },
                })}
                name="answerFavorite"
              />
              {errors.answerFavorite && (
                <ErrorMessage>{errors.answerFavorite.message}</ErrorMessage>
              )}
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>
                3 . 활동중 가장 어려웠던 점은 무엇인가요?
              </LabelText>
              <InputTextarea
                placeholder="파티를 진행하며 힘들었거나 어려웠던 점을 알려주세요."
                ref={register({
                  required: {
                    value: true,
                    message: `활동 중 어려웠던 점을 입력해주세요.`,
                  },
                })}
                name="answerDifficult"
              />
              {errors.answerDifficult && (
                <ErrorMessage>{errors.answerDifficult.message}</ErrorMessage>
              )}
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>
                4 . 후원을 성공적으로 이끌 수 있었던 비결은 무엇인가요?
              </LabelText>
              <InputTextarea
                placeholder="후원을 성공적으로 이끌어낸 본인만의 비결을 알려주세요."
                ref={register({
                  required: {
                    value: true,
                    message: `후원을 성공적으로 이끌어낸 본인만의 비결을 입력해주세요.`,
                  },
                })}
                name="answerSecret"
              />
              {errors.answerSecret && (
                <ErrorMessage>{errors.answerSecret.message}</ErrorMessage>
              )}
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText>
                5 . Party for UNICEF 홍보 방법을 제안해 주세요.
              </LabelText>
              <InputTextarea
                placeholder="더 많은 사람들이 유니세프와 함께할 수 있는 방법을 제안해주세요.(선택)"
                ref={register}
                name="answerSuggestion"
              />
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText>
                6 . 기타 유니세프에 제안하고 싶은 것을 알려주세요.
              </LabelText>
              <InputTextarea
                placeholder="더 나은 유니세프가 되도록 기타 제안사항을 알려주세요. (선택)"
                ref={register}
                name="answerEtc"
              />
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText>
                7 . 즐거웠던 모금 파티 현장 사진을 보여주세요. (최대 5장)
              </LabelText>
              <div
                css={`
                  margin-top: 24px;
                `}
              >
                <AttachmentPreview
                  multiple
                  files={files}
                  onFilesChange={setFiles}
                />
              </div>
              <p className="tip">※첨부파일 (최대 5개의 PDF 형식만 가능)</p>
            </FormGroup>
            <div className="agree-wrap">
              <InputCheckbox
                label="모금 파티 사진과 이야기를 유니세프한국위원회 배포자료, 언론, SNS 등에 공유하는 데 동의합니다. (선택)"
                name="isAgreeYn1"
                ref={register}
              />
              <InputCheckbox
                label="유니세프한국위원회의 다양한 소식들을 앞으로도 계속 더 듣고 싶습니다. (선택)"
                name="isAgreeYn2"
                ref={register}
              />
            </div>

            <div className="btn-wrap">
              <Button type="submit" full>
                제출하기
              </Button>
            </div>
          </article>
        </div>
      </form>
    </StepContainer>
  );
};
export default ParrtAppyStep2;
